import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import "video-react/dist/video-react.css"
import { BigPlayButton, Player } from 'video-react'
import IconBtn from '../../common/IconBtn'
import { markLectureAsComplete } from '../../../services/operations/courseDetailsAPI'
import { updateCompletedLectures } from '../../../slices/viewCourseSlice'

const VideoDetails = () => {

    const { courseId, sectionId, subSectionId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const playerRef = useRef(null)
    const dispatch = useDispatch()
    const { token } = useSelector((state) => state.auth)
    const { courseSectionData, courseEntireData, completedLectures } =
        useSelector((state) => state.viewCourse)

    const [videoData, setVideoData] = useState([])
    const [previewSource, setPreviewSource] = useState("")
    const [videoEnded, setVideoEnded] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        ;(async () => {
        if (!courseSectionData.length) return
        if (!courseId && !sectionId && !subSectionId) {
            navigate(`/dashboard/enrolled-courses`)
        } 
        else {
            // console.log("courseSectionData", courseSectionData)
            const filteredData = courseSectionData.filter(
            (course) => course._id === sectionId
            )
            // console.log("filteredData", filteredData)
            const filteredVideoData = filteredData?.[0]?.subSection?.filter(
            (data) => data._id === subSectionId
            )

            //When the section or lecture in the url is not in the loaded course,
            //this used to read [0] off undefined and take the whole page down with
            //"Cannot read properties of undefined". It happens while a different
            //course is still loading, so an empty state is the right answer
            setVideoData(filteredVideoData?.[0] ?? null)
            setPreviewSource(courseEntireData?.thumbnail ?? "")
            setVideoEnded(false)
        }
        })()
    //These effects are deliberately not re-run on the other values they read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseSectionData, courseEntireData, location.pathname])

    // check if the lecture is the first video of the course
    const isFirstVideo = () => {
        const currentSectionIndx = courseSectionData.findIndex(
        (data) => data._id === sectionId
        )

        //findIndex returns -1 when the section is not in the loaded course, and
        //courseSectionData[-1] is undefined
        if(currentSectionIndx < 0) return true

        const currentSubSectionIndx = courseSectionData[
        currentSectionIndx
        ].subSection.findIndex((data) => data._id === subSectionId)

        if (currentSectionIndx === 0 && currentSubSectionIndx === 0) {
        return true
        } else {
        return false
        }
    }

    // go to the next video
    const goToNextVideo = () => {
        const currentSectionIndx = courseSectionData.findIndex(
        (data) => data._id === sectionId
        )

        if(currentSectionIndx < 0) return

        const noOfSubsections =
        courseSectionData[currentSectionIndx].subSection.length

        const currentSubSectionIndx = courseSectionData[
        currentSectionIndx
        ].subSection.findIndex((data) => data._id === subSectionId)

        // console.log("no of subsections", noOfSubsections)

        if (currentSubSectionIndx !== noOfSubsections - 1) {
        const nextSubSectionId =
            courseSectionData[currentSectionIndx].subSection[
            currentSubSectionIndx + 1
            ]._id
        navigate(
            `/view-course/${courseId}/section/${sectionId}/sub-section/${nextSubSectionId}`
        )
        } else {
        const nextSectionId = courseSectionData[currentSectionIndx + 1]._id
        const nextSubSectionId =
            courseSectionData[currentSectionIndx + 1].subSection[0]._id
        navigate(
            `/view-course/${courseId}/section/${nextSectionId}/sub-section/${nextSubSectionId}`
        )
        }
    }

    // check if the lecture is the last video of the course
    const isLastVideo = () => {
        const currentSectionIndx = courseSectionData.findIndex(
        (data) => data._id === sectionId
        )

        if(currentSectionIndx < 0) return true

        const noOfSubsections =
        courseSectionData[currentSectionIndx].subSection.length

        const currentSubSectionIndx = courseSectionData[
        currentSectionIndx
        ].subSection.findIndex((data) => data._id === subSectionId)

        if (
        currentSectionIndx === courseSectionData.length - 1 &&
        currentSubSectionIndx === noOfSubsections - 1
        ) {
        return true
        } 
        else {
            return false
        }
    }

    // go to the previous video
    const goToPrevVideo = () => {
        const currentSectionIndx = courseSectionData.findIndex(
        (data) => data._id === sectionId
        )

        if(currentSectionIndx < 0) return

        const currentSubSectionIndx = courseSectionData[
        currentSectionIndx
        ].subSection.findIndex((data) => data._id === subSectionId)

        if (currentSubSectionIndx !== 0) {
        const prevSubSectionId =
            courseSectionData[currentSectionIndx].subSection[
            currentSubSectionIndx - 1
            ]._id
        navigate(
            `/view-course/${courseId}/section/${sectionId}/sub-section/${prevSubSectionId}`
        )
        } else {
        const prevSectionId = courseSectionData[currentSectionIndx - 1]._id
        const prevSubSectionLength =
            courseSectionData[currentSectionIndx - 1].subSection.length
        const prevSubSectionId =
            courseSectionData[currentSectionIndx - 1].subSection[
            prevSubSectionLength - 1
            ]._id
        navigate(
            `/view-course/${courseId}/section/${prevSectionId}/sub-section/${prevSubSectionId}`
        )
        }
    }

    const handleLectureCompletion = async () => {
        setLoading(true)
        const res = await markLectureAsComplete(
        { courseId: courseId, subsectionId: subSectionId },
        token
        )
        if (res) {
        dispatch(updateCompletedLectures(subSectionId))
        }
        setLoading(false)
    }

  return (
    <div className="flex flex-col gap-5 text-white">
      {!videoData ? (
        <img
          src={previewSource}
          alt="Preview"
          className="h-full w-full rounded-md object-cover"
        />
      ) : (
        <Player
          ref={playerRef}
          aspectRatio="16:9"
          playsInline
          onEnded={() => setVideoEnded(true)}
          src={videoData?.videoUrl}
        >
          <BigPlayButton position="center" />
          {/* Render When Video Ends */}
          {videoEnded && (
            <div
              style={{
                backgroundImage:
                  "linear-gradient(to top, rgb(0, 0, 0), rgba(0,0,0,0.7), rgba(0,0,0,0.5), rgba(0,0,0,0.1)",
              }}
              className="full absolute inset-0 z-[100] grid h-full place-content-center font-inter"
            >
              {!completedLectures.includes(subSectionId) && (
                <IconBtn
                  disabled={loading}
                  onClick={() => handleLectureCompletion()}
                  text={!loading ? "Mark As Completed" : "Loading..."}
                  customClasses="text-xl max-w-max px-4 mx-auto"
                />
              )}
              <IconBtn
                disabled={loading}
                onClick={() => {
                  if (playerRef?.current) {
                    // set the current time of the video to 0 and play automatically
                    playerRef?.current?.seek(0)
                    setVideoEnded(false)
                    // kick off playback again
                    playerRef.current.actions.play()
                  }
                }}
                text="Rewatch"
                customClasses="text-xl max-w-max px-4 mx-auto mt-2"
              />
              <div className="mt-10 flex min-w-[250px] justify-center gap-x-4 text-xl">
                {!isFirstVideo() && (
                  <button
                    disabled={loading}
                    onClick={goToPrevVideo}
                    className="blackButton"
                  >
                    Prev
                  </button>
                )}
                {!isLastVideo() && (
                  <button
                    disabled={loading}
                    onClick={goToNextVideo}
                    className="blackButton"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          )}
        </Player>
      )}

      <h1 className="mt-4 text-3xl font-semibold">{videoData?.title}</h1>
      <p className="pt-2 pb-6">{videoData?.description}</p>
    </div>
  )
}

export default VideoDetails
